import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
type Host = {
  hostUserId: number | null; day: string; eligible: number; requested: number;
  accepted: number; alreadyQueued: number; failed: number; skipped: number;
  remaining: number; overdue: number; observed: number; pendingObservation: number;
  overdueRoomIds: number[] | null; lastListingEvidence: string | null;
  lastSuggestionEvidence: string | null; executionBookkeepingMissing: number;
};
type Diagnostics = {
  hosts: Host[]; evidenceNotice: string; schedulerNotice: string; feedNotice: string;
  runs: { day: string; batches: number; totals: Record<string, number>; completed_at: string | null }[];
  feedAdvisories: { hostUserId: number; listingEvidenceQuiet: number; suggestionEvidenceQuiet: number;
    listingUnknown: number; suggestionsUnknown: number }[];
};
function feedEvidence(stamp: string | null) {
  if (!stamp) return "No prior evidence · Unknown";
  const days = Math.max(0, Math.floor((Date.now() - new Date(stamp).getTime()) / 86_400_000));
  return `${days}d since evidence${days > 7 ? " · Quiet; investigate only" : ""}`;
}
export function RoomHarvestPanel() {
  const query = useQuery<Diagnostics>({
    queryKey: ["/api/admin/room-harvest-diagnostics"], refetchInterval: 60_000,
  });
  if (query.isLoading) return <p>Loading room harvest evidence…</p>;
  if (query.isError) return <div role="alert">Room harvest evidence unavailable.
    <Button variant="outline" onClick={() => void query.refetch()}>Retry</Button></div>;
  if (!query.data) return null;
  const { hosts, runs } = query.data;
  return <section className="space-y-4" data-testid="admin-room-harvest">
    <div className="flex items-center justify-between gap-3">
      <h2 className="text-lg font-semibold">Daily Room Harvest</h2>
      <Button variant="outline" disabled={query.isFetching} onClick={() => void query.refetch()}>Refresh evidence</Button>
    </div>
    <p className="text-sm text-muted-foreground">{query.data.evidenceNotice}</p>
    <p className="text-sm text-muted-foreground">{query.data.schedulerNotice}</p>
    <p className="text-sm text-muted-foreground">Cycles start at 09:25 UTC; observation deadline 24h; harvest age stale after 48h.
      Dispatch totals include durable attempt records, not confirmed extraction.</p>
    {!runs.length && <p>No daily cycles recorded yet.</p>}
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <caption className="text-left font-medium mb-2">Daily coverage (last 30 cycles)</caption>
        <thead><tr>{["UTC cycle", "Batches started", "Eligible", "Attempted", "Remaining", "Failed attempts", "Overdue"].map((label) =>
          <th key={label} scope="col" className="text-left p-2">{label}</th>)}</tr></thead>
        <tbody>{runs.map((run) => <tr key={run.day} className="border-t">
          <td className="p-2">{run.day}</td><td className="p-2">{run.batches}</td>
          <td className="p-2">{run.totals.eligible ?? 0}</td><td className="p-2">{run.totals.requested ?? 0}</td>
          <td className="p-2">{run.totals.remaining ?? 0}</td><td className="p-2">{run.totals.failedAttempts ?? 0}</td>
          <td className="p-2">{run.totals.overdue ?? 0}</td>
        </tr>)}</tbody>
      </table>
    </div>
    <p className="text-sm text-muted-foreground">{query.data.feedNotice}</p>
    {query.data.feedAdvisories.map((feed) => <p key={feed.hostUserId} className="text-sm text-muted-foreground">
      Host {feed.hostUserId}: missing recent listing evidence {feed.listingEvidenceQuiet}; missing recent
      configured-suggestion evidence {feed.suggestionEvidenceQuiet}; never-seen/unknown listing {feed.listingUnknown},
      suggestions {feed.suggestionsUnknown}. Optional activity only, not a failure signal.
    </p>)}
    {hosts.map((host) => <article key={`${host.day}-${host.hostUserId}`} className="rounded border p-4 space-y-2">
      <h3 className="font-medium">Host {host.hostUserId ?? "unrouted"} · {host.day}</h3>
      <p className="text-sm">Eligible {host.eligible} · Attempted {host.requested} · Accepted {host.accepted} ·
        Already queued {host.alreadyQueued} · Failed {host.failed} · Skipped {host.skipped} · Remaining {host.remaining}</p>
      <p className="text-sm">Generic harvest observed {host.observed} · Awaiting observation {host.pendingObservation} · Overdue {host.overdue}</p>
      {!!host.overdueRoomIds?.length && <p className="text-sm break-words">Overdue room IDs: {host.overdueRoomIds.join(", ")}</p>}
      <p className="text-sm text-muted-foreground">Listing history: {feedEvidence(host.lastListingEvidence)}<br />
        Suggestions: {feedEvidence(host.lastSuggestionEvidence)}<br />
        Missing execution bookkeeping (&gt;24h since sent): {host.executionBookkeepingMissing}. This does not prove changes were not applied.</p>
    </article>)}
  </section>;
}
